import { canFieldRivalTeam, gameSeasons, rivalTeams, type RivalTeam } from './competition';
import { formations, playerById, players } from './data';
import { arrangeLineup, createFreshSave } from './gameState';
import type { Player, SaveGame } from './types';

export const DRAFT_POSITIONS: Player['position'][] = ['GK', 'DF', 'MF', 'FW'];
export const DRAFT_POOL_SIZE = 20;

/**
 * Creates the candidate banks for one Draft setup.
 *
 * The imported catalog deliberately contains one record per team/season. That
 * is correct for the world database, but a Draft needs one person, not every
 * historical copy of that person. We retain the first catalog appearance as
 * the canonical profile and sample it only once across the four banks.
 */
export function createDraftPoolByPosition(random: () => number = Math.random): Map<Player['position'], Player[]> {
  const canonicalPlayers = uniqueDraftPlayers().filter(hasAssignedTeam);

  return new Map(DRAFT_POSITIONS.map((position) => [
    position,
    sampleWithoutReplacement(
      canonicalPlayers.filter((player) => player.position === position),
      DRAFT_POOL_SIZE,
      random,
    ),
  ]));
}

/** Classic mode only offers clubs that can field a complete starting eleven.
 * They are grouped in the same chronological order used by the game seasons. */
export const classicTeams = rivalTeams
  .filter(canFieldRivalTeam)
  .sort((first, second) => {
    const firstSeason = gameSeasons.indexOf(first.games[0]);
    const secondSeason = gameSeasons.indexOf(second.games[0]);
    return firstSeason - secondSeason || first.name.localeCompare(second.name);
  });

export function createClassicSave(team: RivalTeam): SaveGame {
  const ownedPlayerIds = uniquePlayerIds(team.players.map((player) => player.id)).slice(0, 30);
  const formationId = formations[0].id;
  const lineup = arrangeLineup(formationId, ownedPlayerIds) ?? ownedPlayerIds.slice(0, 11);
  const benchPlayerIds = ownedPlayerIds.filter((id) => !lineup.includes(id)).slice(0, 7);
  if (lineup.length !== 11) throw new Error('El equipo elegido no dispone de un once válido.');
  return createBaseSave({
    gameMode: 'classic',
    clubName: team.name,
    selectedTeamId: team.id,
    clubCrestId: team.id,
    ownedPlayerIds,
    lineup,
    benchPlayerIds,
    captainPlayerId: lineup[0],
    formationId,
  });
}

export function createDraftSave(playerIds: string[], clubName: string, clubCrestId = 'draft_default'): SaveGame {
  const ownedPlayerIds = uniquePlayerIds(playerIds).filter((id) => playerById.has(id));
  if (ownedPlayerIds.length !== 22) throw new Error('El draft debe contener exactamente 22 jugadores únicos.');
  const formationId = formations[0].id;
  const lineup = arrangeLineup(formationId, ownedPlayerIds);
  if (!lineup) throw new Error('El draft necesita jugadores suficientes para construir un once.');
  const benchPlayerIds = ownedPlayerIds.filter((id) => !lineup.includes(id)).slice(0, 7);
  return createBaseSave({
    gameMode: 'draft',
    clubName: clubName.trim().slice(0, 32) || 'Equipo Draft',
    clubCrestId,
    ownedPlayerIds,
    lineup,
    benchPlayerIds,
    captainPlayerId: lineup[0],
    formationId,
  });
}

function createBaseSave(overrides: Partial<SaveGame>): SaveGame {
  const base = createFreshSave();
  return {
    ...base,
    ...overrides,
    version: base.version,
    ownedPlayerIds: [...(overrides.ownedPlayerIds ?? base.ownedPlayerIds)],
    lineup: [...(overrides.lineup ?? base.lineup)],
    benchPlayerIds: [...(overrides.benchPlayerIds ?? base.benchPlayerIds)],
    customFormations: [],
    tactics: { ...base.tactics },
    tokens: { ...base.tokens },
    recruits: [],
    leagueResults: [],
    lastResult: null,
    spiritReleasedPlayerIds: [],
  };
}

function uniquePlayerIds(ids: string[]) {
  return [...new Set(ids)];
}

function uniqueDraftPlayers() {
  const seenIdentities = new Set<string>();
  return players.filter((player) => {
    const identity = playerIdentityKey(player);
    if (seenIdentities.has(identity)) return false;
    seenIdentities.add(identity);
    return true;
  });
}

function hasAssignedTeam(player: Player) {
  const team = player.metadata?.team?.trim().toLocaleLowerCase() ?? '';
  return team.length > 0 && team !== 'unaffiliated' && team !== 'unknown' && team !== 'sub character';
}

function playerIdentityKey(player: Player) {
  return player.name
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9]/g, '')
    .toLocaleLowerCase();
}

function sampleWithoutReplacement(candidates: Player[], amount: number, random: () => number) {
  const shuffled = [...candidates];
  for (let index = shuffled.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(Math.max(0, Math.min(.999999999, random())) * (index + 1));
    [shuffled[index], shuffled[swapIndex]] = [shuffled[swapIndex], shuffled[index]];
  }
  return shuffled.slice(0, amount);
}
